<template lang="">
  <div class="page-main__content" v-if="isEditingAllowed">
    <div class="page-main__content">
      <ProjectSourceBanner v-if="showBanner"
        :project="project">
      </ProjectSourceBanner>
      <ProjectSourceBanner v-if="hasContentInErrorState > 0"
        banner-icon="error"
        banner-title="AI Generation Error"
        banner-description="One or more of the video scripts that you have generated has an error. Select the &quot;?&quot; tooltip for further details."
        :project="project">
      </ProjectSourceBanner>
      <div class="app-filters">
        <div v-if="aiEnabled" class="app-filters__buttons">
          <a
            :href="generateVideosLocation"
            class="element-button--ai-primary element-button">
            <svg class="element-icon--ai element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-ai"></use></svg>
            Generate Video Script with AI
          </a>
        </div>
      </div>
      <div class="element-table__wrapper">
        <table>
          <thead>
            <tr>
              <th>Title
                <div data-bs-toggle="dropdown" class="element-chip is--ai">
                  <svg class="element-icon--ai element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-ai"></use></svg>
                  AI
                </div>
                <div class="app-tooltip--generate-status app-tooltip dropdown-menu">
                  <h4 class="app-tooltip__title">AI Generated</h4>
                  <div class="app-tooltip__body">
                    <p>We’ve used AI to generate this content based on the information provided.</p>
                  </div>
                </div>
              </th>
              <th>
                Status
                <svg data-bs-toggle="dropdown" class="element-icon--tooltip-help element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-tooltip-help"></use></svg>
                <div class="app-tooltip--generate-status app-tooltip dropdown-menu">
                  <h4 class="app-tooltip__title">Status Definitions</h4>
                  <div class="app-tooltip__body">
                    <ul>
                      <li v-for="video in staticStatuses">
                        <strong>{{ video.label }}:</strong> {{ video.description }}
                      </li>
                    </ul>
                  </div>
                </div>
              </th>
              <th>Created By</th>
              <th>Created Date</th>
              <th></th>
            </tr>
          </thead>
          <tbody v-if="generatedVideos.length">
            <tr v-for="video in generatedVideos" :key="video.id">
              <td class="element-bold">
                <div class="app-publications-list__title-cell">
                  <span class="element-featured-icon is--sm">
                    <svg class="element-icon--ai element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-ai"></use></svg>
                  </span>
                  {{ video.name }}
                </div>
              </td>
              <td>
                <div v-if="hasProgressBar(video)" class="element-progress">
                  <div class="element-progress__text">{{ getVideoScriptsStatusLabel(video) }}</div>
                  <div class="element-progress__bar">
                    <div
                      class="element-progress__bar-filled"
                      role="progressbar"
                      aria-valuenow="0"
                      aria-valuemin="0"
                      aria-valuemax="100"
                      :style="`width: ${getProgressPercentage(video)}%;`">
                    </div>
                  </div>
                </div>
                <div v-else class="app-videos-list__statuses">
                  <span :class="['element-badge', getStatusClass(video)]">{{ getVideoScriptsStatusLabel(video) }}</span>
                  <svg v-if="['error'].includes(getStatus(video))" data-bs-toggle="dropdown" class="element-icon--tooltip-help element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-tooltip-help"></use></svg>
                  <div class="app-tooltip--generate-status app-tooltip dropdown-menu">
                    <h4 class="app-tooltip__title">Error</h4>
                    <div class="app-tooltip__body">
                      <p>{{ getVideoScriptsStatusLabel(video, true) }}</p>
                    </div>
                  </div>
                </div>
              </td>
              <td>{{ video.created_by.name }}</td>
              <td>{{ localizeDate(video.created_at).format('MMM D, YYYY h:mm A') }}</td>
              <td class="element-table__actions">
                <template v-if="!['in-progress--content-generating', 'completed'].includes(getStatus(video)) && !hasBasicAccess && aiEnabled">
                  <a class="element-button--secondary element-button" :href="`/projects/${project.id}/videoscripts/${video.id}/edit`">Edit</a>
                </template>
                <template v-if="['completed'].includes(getStatus(video))">
                  <div>
                    <a class="element-button--secondary element-button" :href="`/projects/${project.id}/videoscripts/${video.id}/download`" target="_blank">Download</a>
                  </div>
                </template>
                <template v-if="isDeleteAllowed(video)">
                  <div @click="$event.isAction = true" class="dropdown">
                    <button class="element-table__actions-button element-button--icon element-button" type="button" data-bs-toggle="dropdown">
                        <svg class="element-icon--more-info element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-more-info"></use></svg>
                    </button>
                    <div class="app-dropdown dropdown-menu dropdown-menu-right">
                        <app-button
                            class="is--alert"
                            @click="remove(video)">
                            <svg class="element-icon--delete element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-delete"></use></svg>
                            Delete
                        </app-button>
                    </div>
                  </div>
                </template>
              </td>
            </tr>
          </tbody>
        </table>

        <div v-if="!generatedVideos.length" class="empty-table element-empty-instructions">
          <img src="/img/undraw_runner_start_x-0-uu.svg" />
          <p>No video scripts have been added.</p>
        </div>
      </div>
    </div>
  </div>

  <project-sources-splash v-if="!isEditingAllowed"
    :project="project"
    :project-source="projectSource"
    :has-golden-source="hasGoldenSource"
    :is-analyzing="isAnalyzing">
  </project-sources-splash>

</template>
<script>
import { localizeDate } from '../helpers/util'
import api from '../api/publisher'
import ProjectSourceBanner from './ProjectSourceBanner'
import { mapGetters } from 'vuex'

export default {
  name: 'VideoScripts',
  components: { ProjectSourceBanner },
  props: {
    videoScripts: {
      type: Array,
      default: () => []
    },
    project: {
      type: Object
    },
    aiEnabled: {
      type: [Boolean, Number],
      default: false
    },
    showBanner: {
      type: Boolean,
      default: true
    },
    hasContentInErrorState: {
      type: Number,
      default: 0
    },
  },
  data() {
    return {
      projectSource: this.project.project_source || null,
      generatedVideos: this.videoScripts,
      generateVideosLocation: `/projects/${this.project.id}/videoscripts/create`,
    }
  },
  computed: {
    ...mapGetters([
        'hasBasicAccess'
    ]),
    isEditingAllowed() {
      return this.hasGoldenSource && !this.isAnalyzing && !this.hasGoldenSourceError
    },
    isAnalyzing() {
      return (this.projectSource && this.projectSource.is_analyzing) ? true : false
    },
    hasGoldenSource() {
      return (this.projectSource && this.projectSource.completed_at) ? true : false 
    },
    hasGoldenSourceError() {
      return (this.projectSource && this.projectSource.errors) ? true : false
    },
    staticStatuses() {
      return [
        { label: this.getStatusLabel('new'), description: this.getStatusLabel('new', true) },
        { label: this.getStatusLabel('in-progress--content-generating'), description: this.getStatusLabel('in-progress--content-generating', true) },
        { label: this.getStatusLabel('completed'), description: this.getStatusLabel('completed', true) },
      ];
    },
  },
  methods: {
    localizeDate,
    getStatus(video) {
      return _.get(video, 'ai_preferences.status', "new")
    },    
    getProgressPercentage(video) {
      let val = _.get(video, 'ai_preferences.progress', 0)
      return (val * 100)
    },
    isDeleteAllowed(publication) {
      if (this.hasBasicAccess)
        return false

      return !['in-progress--content-generating'].includes(this.getStatus(publication))
    },
    async remove(video) {
      if (!confirm('Are you sure you want to delete this Video Script?')) {
        return
      }

      // remove the element from UI
      const index = this.generatedVideos.findIndex((item) => item.id === video.id);
      if (index !== -1)
        this.generatedVideos.splice(index, 1)

      // api call to save
      const result = await api.deleteVideoScript(this.project.id, video.id)

      if (result.errors) {
        console.alert('Error deleting Video Script: ' + result.errors.join(', '))
      } else {

      }
    },
    hasProgressBar(video) {
      const status = this.getStatus(video)
      if (['in-progress--content-generating'].includes(status)) {
        return true
      }
      return false
    },
    getProgressPercentage(video) {
      let val = _.get(video, 'ai_preferences.progress', 0)
      return (val * 100)
    },
    getVideoScriptsStatusLabel(video, description = false) {
      const status = this.getStatus(video)
      if (status === 'error' && description) // for error description retrun actual error message not static message
        return this.getError(video)
      return this.getStatusLabel(status, description)
    },
    getStatusClass(video) {
      const status = this.getStatus(video)
      switch (status) {
        case 'error':
          return 'is--error'
        case 'completed':
          return 'is--success'
        case 'new':
        case 'draft':
          return 'is--info'
        case 'in-progress--content-generating':
          return 'is--processing'
        default:
          return ''
      }
    },
    getStatusLabel(status, description = false) {
      switch (status) {
        case 'draft':
        case 'new':
          if (description)
            return 'New video script has been created and is ready for generation'
          return 'New'
        case 'in-progress--content-generating':
          if (description)
            return 'Content generation is currently in progress, please wait...'
          return 'Content Generating'
        case 'completed':
          if (description)
            return 'Content has been generated successfully'
          return 'Complete'
        case 'error':
        case 'failed':
          return 'Error'
      }
      return status
    },
  },
  created() {

  },
}
</script>
<style lang="">
    
</style>